import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from '../../components/ui/use-toast';
import { apiRequest, API_BASE_URL } from '../../config/api';
import IndianMobileInput from '../../components/common/IndianMobileInput';
import EmailInput from '../../components/common/EmailInput';
import { getIndianMobileError } from '../../utils/phone';
import { getEmailError } from '../../utils/email';
import { Stethoscope, Lock, User, Building2, MapPin, CheckCircle2, Clock, Eye, EyeOff, Upload, Image, X } from 'lucide-react';

const RegisterHospitalPage = () => {
  // Logo path - use logo-purple.png from public folder, fallback to current icon if not available
  const logoPath = '/logo-purple.png';
  const [logoError, setLogoError] = useState(false);

  const [formData, setFormData] = useState({
    hospital_name: '',
    hospital_email: '',
    hospital_phone: '',
    hospital_address: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredHospital, setRegisteredHospital] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({
    hospital_phone: null,
    hospital_email: null,
    admin_email: null
  });
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload an image file (JPG, PNG, GIF, or WebP)',
          variant: 'destructive'
        });
        return;
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast({
          title: 'File too large',
          description: 'Logo file must be less than 5MB',
          variant: 'destructive'
        });
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    // Reset file input
    const fileInput = document.getElementById('hospital_logo');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate all fields
    const newErrors = {
      hospital_phone: getIndianMobileError(formData.hospital_phone, true),
      hospital_email: getEmailError(formData.hospital_email, true), // Required
      admin_email: getEmailError(formData.admin_email, true) // Required
    };
    
    setErrors(newErrors);

    // Check for validation errors
    const hasErrors = Object.values(newErrors).some(error => error !== null);

    // Password validation
    if (formData.admin_password !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      setLoading(false);
      return;
    }

    if (formData.admin_password.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters long',
        variant: 'destructive'
      });
      setLoading(false);
      return;
    }

    // Required field validation
    if (!formData.hospital_name || !formData.admin_name) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      setLoading(false);
      return;
    }

    // Check validation errors
    if (hasErrors) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors in the form',
        variant: 'destructive'
      });
      setLoading(false);
      return;
    }

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('hospital_name', formData.hospital_name);
      formDataToSend.append('hospital_email', formData.hospital_email);
      formDataToSend.append('hospital_phone', formData.hospital_phone || '');
      formDataToSend.append('hospital_address', formData.hospital_address || '');
      formDataToSend.append('admin_name', formData.admin_name);
      formDataToSend.append('admin_email', formData.admin_email);
      formDataToSend.append('admin_password', formData.admin_password);
      
      // Append logo file if selected
      if (logoFile) {
        formDataToSend.append('hospital_logo', logoFile);
      }

      // Use the API_BASE_URL imported from api.js (already configured correctly)
      // It uses /api/ for localhost:8001 (PHP built-in server with -t backend)
      const token = localStorage.getItem('session_token');

      let response;
      let responseData;
      
      try {
        response = await fetch(`${API_BASE_URL}/register_hospital.php`, {
          method: 'POST',
          headers: token ? {
            'Authorization': `Bearer ${token}`
          } : {},
          body: formDataToSend
        });
      } catch (fetchError) {
        // Handle network errors (connection refused, etc.)
        if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('ERR_CONNECTION_REFUSED') || fetchError.message.includes('network')) {
          throw new Error('Cannot connect to the server. Please ensure the backend server is running on http://localhost:8001');
        }
        throw fetchError;
      }

      // Try to parse JSON response
      try {
        const text = await response.text();
        if (!text) {
          throw new Error('Empty response from server');
        }
        responseData = JSON.parse(text);
      } catch (jsonError) {
        // If parsing fails, it might be an HTML error page
        console.error('Failed to parse response as JSON:', jsonError);
        throw new Error('Invalid response from server. Please check if the backend server is running correctly and the endpoint exists.');
      }

      if (!response.ok) {
        throw new Error(responseData.error || responseData.message || `Registration failed (${response.status})`);
      }

      if (responseData.success) {
        setRegisteredHospital(responseData.hospital);
        setRegistrationSuccess(true);
        toast({
          title: 'Registration Successful!',
          description: 'Your hospital registration is pending approval.',
        });
      } else {
        throw new Error(responseData.error || responseData.message || 'Registration failed');
      }
    } catch (error) {
      // Handle connection errors specifically
      const errorMessage = error.message || 'An unexpected error occurred. Please try again.';
      
      if (errorMessage.includes('Cannot connect to the server') || 
          errorMessage.includes('Failed to fetch') || 
          errorMessage.includes('ERR_CONNECTION_REFUSED') || 
          errorMessage.includes('network')) {
        toast({
          title: 'Connection Error',
          description: 'Cannot connect to the backend server. Please ensure the PHP server is running on http://localhost:8001. You can start it with: php -S localhost:8001 -t backend',
          variant: 'destructive'
        });
        setLoading(false);
        return;
      }

      // Handle duplicate email/phone errors from backend
      let displayErrorMessage = errorMessage;
      
      // Try to parse error response if available
      if (error.response) {
        try {
          const errorData = typeof error.response === 'string' ? JSON.parse(error.response) : error.response;
          if (errorData.field === 'hospital_email') {
            setErrors(prev => ({ ...prev, hospital_email: errorData.error || displayErrorMessage }));
            displayErrorMessage = errorData.error || displayErrorMessage;
          } else if (errorData.field === 'hospital_phone') {
            setErrors(prev => ({ ...prev, hospital_phone: errorData.error || displayErrorMessage }));
            displayErrorMessage = errorData.error || displayErrorMessage;
          } else if (errorData.error) {
            displayErrorMessage = errorData.error;
          }
        } catch (parseError) {
          // If response is not JSON, check if message contains keywords
          if (typeof error.message === 'string') {
            if (error.message.includes('email is already registered') || error.message.includes('email already')) {
              setErrors(prev => ({ ...prev, hospital_email: 'This email is already registered with another hospital' }));
            } else if (error.message.includes('phone number is already registered') || error.message.includes('phone already')) {
              setErrors(prev => ({ ...prev, hospital_phone: 'This phone number is already registered with another hospital' }));
            }
          }
        }
      } else if (typeof error.message === 'string') {
        // Check error message for duplicate keywords
        if (error.message.includes('email is already registered') || error.message.includes('email already')) {
          setErrors(prev => ({ ...prev, hospital_email: 'This email is already registered with another hospital' }));
          displayErrorMessage = 'This email is already registered with another hospital';
        } else if (error.message.includes('phone number is already registered') || error.message.includes('phone already')) {
          setErrors(prev => ({ ...prev, hospital_phone: 'This phone number is already registered with another hospital' }));
          displayErrorMessage = 'This phone number is already registered with another hospital';
        }
      }
      
      toast({
        title: 'Registration Failed',
        description: displayErrorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (registrationSuccess) {
    return (
      <>
        <Helmet>
          <title>Registration Successful - Dental Hub</title>
          <meta name="description" content="Your hospital registration is pending approval." />
        </Helmet>
        
        <div className="min-h-screen flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-2xl"
          >
            <Card className="glass-effect border-gray-700">
              <CardHeader className="text-center p-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="mx-auto mb-4 w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center"
                >
                  <CheckCircle2 className="h-10 w-10 text-green-400" />
                </motion.div>
                <CardTitle className="text-2xl text-white mb-2">Registration Successful!</CardTitle>
                <CardDescription className="text-gray-300">
                  Your hospital registration has been submitted
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div className="glass-effect rounded-lg p-6 border border-blue-500/30">
                  <div className="flex items-start space-x-4">
                    <Clock className="h-6 w-6 text-blue-400 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">Pending Approval</h3>
                      <p className="text-gray-300 mb-4">
                        Your hospital registration is currently pending approval by our admin team. 
                        You will receive an email notification once your account has been approved.
                      </p>
                      {registeredHospital && (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Hospital Name:</span>
                            <span className="text-white font-medium">{registeredHospital.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Email:</span>
                            <span className="text-white font-medium">{registeredHospital.email}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Status:</span>
                            <span className="text-blue-400 font-medium uppercase">{registeredHospital.status}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-2">What happens next?</h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      Our admin team will review your registration
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      You'll receive an email notification when approved
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      Once approved, you can login and start using Dental Hub
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      You'll get a 7-day free trial to explore all features
                    </li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button
                    onClick={() => navigate('/')}
                    variant="outline"
                    className="flex-1 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white"
                  >
                    Back to Home
                  </Button>
                  <Button
                    onClick={() => {
                      setRegistrationSuccess(false);
                      setFormData({
                        hospital_name: '',
                        hospital_email: '',
                        hospital_phone: '',
                        hospital_address: '',
                        admin_name: '',
                        admin_email: '',
                        admin_password: '',
                        confirmPassword: ''
                      });
                      setLogoFile(null);
                      setLogoPreview(null);
                    }}
                    className="flex-1 medical-gradient text-white hover:opacity-90"
                  >
                    Register Another Hospital
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Register Hospital - Dental Hub</title>
        <meta name="description" content="Register your hospital or clinic to start using Dental Hub's comprehensive EHR system." />
      </Helmet>
      
      <div className="min-h-screen flex items-center justify-center p-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-3xl"
        >
          <Card className="glass-effect border-gray-700">
            <CardHeader className="text-center p-6">
              <div className="flex items-center justify-center mb-4">
                {!logoError ? (
                  <img 
                    src={logoPath} 
                    alt="Dental Hub Logo" 
                    className="h-16 w-auto object-contain"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <Stethoscope className="h-8 w-8 text-blue-400" />
                    <span className="text-2xl font-bold gradient-text">Dental Hub</span>
                  </div>
                )}
              </div>
              <CardTitle className="text-2xl text-white">Register Your Hospital</CardTitle>
              <CardDescription className="text-gray-300">
                Join Dental Hub and transform your dental practice management
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Hospital Information Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <Building2 className="h-5 w-5 text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">Hospital Information</h3>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="hospital_name" className="text-white">
                      Hospital/Clinic Name <span className="text-red-400">*</span>
                    </Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="hospital_name"
                        name="hospital_name"
                        type="text"
                        placeholder="Enter hospital or clinic name"
                        value={formData.hospital_name}
                        onChange={handleChange}
                        className="pl-10 bg-gray-800 border-gray-600 text-white"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <EmailInput
                      id="hospital_email"
                      name="hospital_email"
                      label="Hospital Email"
                      placeholder="contact@hospital.com"
                      value={formData.hospital_email}
                      onChange={handleChange}
                      required={true}
                      error={errors.hospital_email}
                    />

                    <IndianMobileInput
                      id="hospital_phone"
                      name="hospital_phone"
                      label="Hospital Phone"
                      placeholder="Enter 10-digit mobile number"
                      value={formData.hospital_phone}
                      onChange={handleChange}
                      required={true}
                      error={errors.hospital_phone}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="hospital_address" className="text-white">
                        Hospital Address
                      </Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                        <Textarea
                          id="hospital_address"
                          name="hospital_address"
                          placeholder="123 Main St, City, State, PIN Code"
                          value={formData.hospital_address}
                          onChange={handleChange}
                          className="pl-10 bg-gray-800 border-gray-600 text-white min-h-[100px] resize-y"
                          rows={4}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hospital_logo" className="text-white">
                        Hospital Logo
                      </Label>
                      <div className="space-y-2">
                        {logoPreview ? (
                          <div className="relative">
                            <div className="relative border-2 border-gray-600 rounded-lg p-2 bg-gray-800">
                              <img 
                                src={logoPreview} 
                                alt="Logo preview" 
                                className="w-full h-32 object-contain rounded"
                              />
                              <button
                                type="button"
                                onClick={handleRemoveLogo}
                                className="absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 rounded-full text-white transition-colors"
                                aria-label="Remove logo"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">{logoFile?.name}</p>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center hover:border-blue-500 transition-colors">
                            <label htmlFor="hospital_logo" className="cursor-pointer">
                              <div className="flex flex-col items-center space-y-2">
                                <Image className="h-8 w-8 text-gray-400" />
                                <div className="text-sm text-gray-300">
                                  <span className="text-blue-400 hover:text-blue-300">Click to upload</span> or drag and drop
                                </div>
                                <p className="text-xs text-gray-400">JPG, PNG, GIF or WebP (MAX. 5MB)</p>
                              </div>
                            </label>
                            <input
                              id="hospital_logo"
                              name="hospital_logo"
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                              onChange={handleLogoChange}
                              className="hidden"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-700 my-6"></div>

                {/* Admin User Information Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <User className="h-5 w-5 text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">Administrator Account</h3>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="admin_name" className="text-white">
                      Administrator Name <span className="text-red-400">*</span>
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="admin_name"
                        name="admin_name"
                        type="text"
                        placeholder="Dr. John Smith"
                        value={formData.admin_name}
                        onChange={handleChange}
                        className="pl-10 bg-gray-800 border-gray-600 text-white"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <EmailInput
                      id="admin_email"
                      name="admin_email"
                      label="Administrator Email"
                      placeholder="admin@hospital.com"
                      value={formData.admin_email}
                      onChange={handleChange}
                      required={true}
                      error={errors.admin_email}
                    />
                    <p className="text-xs text-gray-400">
                      This will be your login email for the hospital account
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="admin_password" className="text-white">
                        Password <span className="text-red-400">*</span>
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="admin_password"
                          name="admin_password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Create a password"
                          value={formData.admin_password}
                          onChange={handleChange}
                          className="pl-10 pr-10 bg-gray-800 border-gray-600 text-white"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-300 focus:outline-none"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-gray-400">Minimum 6 characters</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-white">
                        Confirm Password <span className="text-red-400">*</span>
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          className="pl-10 pr-10 bg-gray-800 border-gray-600 text-white"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-300 focus:outline-none"
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <p className="text-sm text-gray-300">
                    <strong className="text-white">Note:</strong> Your account will be created but will remain inactive until approved by our admin team. 
                    You'll receive an email notification once your registration is approved.
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full medical-gradient text-white hover:opacity-90"
                  disabled={loading}
                >
                  {loading ? 'Registering...' : 'Register Hospital'}
                </Button>

                <div className="text-center">
                  {/* <p className="text-gray-400 text-sm">
                    Already have an account?{' '}
                    <Link to="/hospital/login" className="text-blue-400 hover:text-blue-300 underline">
                      Sign in
                    </Link>
                  </p> */}
                  <p className="text-gray-400 text-sm mt-2">
                    <Link to="/" className="text-blue-400 hover:text-blue-300 underline">
                      ← Back to Home
                    </Link>
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </>
  );
};

export default RegisterHospitalPage;

